import { cva } from '@/styled-system/css';

export const dateInputRecipe = cva({
  base: {
    'color': 'DateInput.text.populated',
    'backgroundColor': {
      base: 'DateInput.bg.default',
    },
    'cursor': 'text',
    'textStyle': 'body',
    '_placeholder': {
      color: 'DateInput.text.placeholder',
      textTransform: 'uppercase',
    },
    '&.edge-autofilled, &:-webkit-autofill, &:autofill': {
      // Override user agent stylesheet autofill background
      // This also preserves the border color
      boxShadow: {
        base: 'inset 0 0 0 {borderWidths.DateInput.default} var(--shadow-color), inset 0 0 0 1000px {colors.DateInput.bg.default}',
      },
    },
    'transition': {
      _motionReduce: 'none',
      _motionSafe: 'box-shadow 200ms ease, background-color 200ms ease',
    },
  },
  variants: {
    disabled: {
      true: {
        opacity: 'disabled',
        cursor: 'default',
      },
      false: {
        'backgroundColor': {
          _hover: 'DateInput.bg.hover',
          _focus: 'DateInput.bg.active',
          _active: 'DateInput.bg.active',
        },
        'boxShadow': {
          _focusVisible:
            'inset 0 0 0 {borderWidths.DateInput.default} var(--shadow-color), {shadows.focus}',
        },
        'boxShadowColor': {
          _hover: 'DateInput.border.hover',
          _focus: 'DateInput.border.active',
          _active: 'DateInput.border.active',
        },
        '&.edge-autofilled, &:-webkit-autofill, &:autofill': {
          // Override user agent stylesheet autofill background
          // This also preserves the border color
          boxShadow: {
            _hover:
              'inset 0 0 0 {borderWidths.Input.default} var(--shadow-color), inset 0 0 0 1000px {colors.Input.bg.hover}',
            _focus:
              'inset 0 0 0 {borderWidths.Input.default} var(--shadow-color), {shadows.focus}, inset 0 0 0 1000px {colors.Input.bg.active}',
            _active:
              'inset 0 0 0 {borderWidths.Input.default} var(--shadow-color), inset 0 0 0 1000px {colors.Input.bg.active}',
          },
        },
        '&:is(:hover:active), &:is(:hover:focus)': {
          backgroundColor: 'DateInput.bg.activeHover',
        },
      },
    },
    error: {
      true: {},
      false: {},
    },
    fluid: {
      true: {
        width: '100%',
      },
      false: {
        width: 'auto',
      },
    },
    readOnly: {
      true: {
        backgroundColor: {
          base: 'Input.bg.readOnly',
        },
        boxShadowColor: 'transparent.invisible',
      },
      false: {},
    },
    size: {
      sm: {
        borderRadius: 'DateInput.sm',
        paddingBlock: 'DateInput.sm.padding.vert',
        paddingInline: 'DateInput.sm.padding.horz',
      },
      md: {
        borderRadius: 'DateInput.md.default',
        paddingBlock: 'DateInput.md.padding.vert',
        paddingInline: 'DateInput.md.padding.horz',
      },
      lg: {
        borderRadius: 'DateInput.lg.default',
        paddingBlock: 'DateInput.lg.padding.vert',
        paddingInline: 'DateInput.lg.padding.horz',
      },
    },
    hasContext: {
      true: {},
      false: {
        boxShadow: {
          base: 'inset 0 0 0 {borderWidths.DateInput.default} var(--shadow-color)',
          _autofill:
            'inset 0 0 0 {borderWidths.DateInput.default} var(--shadow-color), inset 0 0 0 1000px {colors.DateInput.bg.autofill}',
        },
        boxShadowColor: {
          base: 'DateInput.border.default',
        },
      },
    },
  },
  compoundVariants: [
    {
      readOnly: false,
      hasContext: false,
      css: {
        backgroundColor: { base: 'DateInput.bg.default' },
      },
    },
    {
      disabled: false,
      error: false,
      css: {
        boxShadowColor: {
          _focusVisible: 'DateInput.border.active',
        },
      },
    },
    {
      readOnly: false,
      disabled: false,
      hasContext: false,
      css: {
        backgroundColor: {
          _hover: 'DateInput.bg.hover',
          _active: 'DateInput.bg.active',
          _focusVisible: 'DateInput.bg.active',
        },
        boxShadowColor: {
          _hover: 'DateInput.border.hover',
          _active: 'DateInput.border.active',
        },
      },
    },
    {
      readOnly: true,
      disabled: false,
      css: {
        backgroundColor: {
          base: 'DateInput.bg.readOnly',
          _active: 'DateInput.bg.readOnly',
          _focusVisible: 'DateInput.bg.readOnly',
          _hover: 'DateInput.bg.readOnly',
        },
        boxShadowColor: {
          base: 'DateInput.border.readOnly',
          _active: 'DateInput.border.readOnly',
          _focusVisible: 'DateInput.border.readOnly',
          _hover: 'DateInput.border.readOnly',
        },
      },
    },
    {
      error: true,
      disabled: false,
      hasContext: false,
      readOnly: false,
      css: {
        'backgroundColor': {
          base: 'DateInput.bg.error',
          _hover: 'DateInput.bg.errorHover',
          _active: 'DateInput.bg.errorActive',
          _focusVisible: 'DateInput.bg.errorActive',
        },
        'boxShadowColor': {
          base: 'DateInput.border.error',
          _hover: 'DateInput.border.errorHover',
        },
        '&.edge-autofilled, &:-webkit-autofill, &:autofill': {
          // Override user agent stylesheet autofill background
          // This also preserves the border color
          boxShadow: {
            _hover:
              'inset 0 0 0 {borderWidths.Input.default} var(--shadow-color), inset 0 0 0 1000px {colors.Input.bg.errorHover}',
            _focus:
              'inset 0 0 0 {borderWidths.Input.default} var(--shadow-color), {shadows.focus}, inset 0 0 0 1000px {colors.Input.bg.errorActive}',
            _active:
              'inset 0 0 0 {borderWidths.Input.default} var(--shadow-color), inset 0 0 0 1000px {colors.Input.bg.errorActive}',
          },
        },
      },
    },
    {
      error: true,
      disabled: false,
      css: {
        boxShadowColor: {
          _active: 'DateInput.border.errorActive',
          _focusVisible: 'DateInput.border.errorActive',
        },
      },
    },
    {
      size: 'sm',
      readOnly: true,
      css: {
        paddingInline: 'DateInput.sm.padding.horz-readOnly',
      },
    },
    {
      size: 'md',
      readOnly: true,
      css: {
        paddingInline: 'DateInput.md.padding.horz-readOnly',
      },
    },
    {
      size: 'lg',
      readOnly: true,
      css: {
        paddingInline: 'DateInput.lg.padding.horz-readOnly',
      },
    },
  ],
});
